type DropzoneFileItem = File | DataTransferItem;

type DropzoneEvent =
  | Event
  | { type?: string; dataTransfer?: DataTransfer | null; target?: EventTarget | null }
  | Array<{ getFile: () => Promise<File> }>;

const FALLBACK_DRAG_MIME = 'application/octet-stream';

function dragItemWithFallbackType(item: DataTransferItem): DataTransferItem {
  if (item.type) return item;
  return { kind: item.kind, type: FALLBACK_DRAG_MIME } as DataTransferItem;
}

/**
 * Reads files from dropzone events without the File System Access API.
 * Chromium kills the tab (RESULT_CODE_KILLED_BAD_MESSAGE) when
 * `DataTransferItem.getAsFileSystemHandle()` runs in an insecure context (e.g. LAN IP over HTTP),
 * and the bundled react-dropzone-esm calls it on every drop.
 */
export async function getFilesFromEvent(event: DropzoneEvent): Promise<DropzoneFileItem[]> {
  if (Array.isArray(event)) {
    return Promise.all(event.map((handle) => handle.getFile()));
  }

  const dataTransfer = 'dataTransfer' in event ? event.dataTransfer : null;
  if (dataTransfer) {
    if (event.type === 'drop') return Array.from(dataTransfer.files);
    // Drag events expose only kind/type; empty MIME types (common for CAD/BIM) would show a false reject.
    return Array.from(dataTransfer.items)
      .filter((item) => item.kind === 'file')
      .map(dragItemWithFallbackType);
  }

  const target = 'target' in event ? event.target : null;
  if (target instanceof HTMLInputElement && target.files) {
    return Array.from(target.files);
  }
  return [];
}
